import { useQuery } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  notFound,
} from '@tanstack/react-router';
import { Fragment } from 'react';
import { z } from 'zod';

import { STDMapSchema } from '@/api';
import { usePatchStdMapId, useStdMap, useStdMapId } from '@/api/queries/std';
import { Button } from '@/components/Button/Button';
import { DropdownMenu } from '@/components/DropdownMenu/DropdownMenu';
import { Explorer } from '@/components/Explorer';
import { ModalTrigger } from '@/components/Modal';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { TextButton } from '@/components/TextButton/TextButton';
import { RegularChevronRight } from '@/icons/components';
import { StandaardtekstSectieAdd } from '@/modals/StandaardtekstSectieAdd';
import { StandaardtekstSectieEdit } from '@/modals/StandaardtekstSectieEdit';
import { useIsExactMatch } from '@/utils/hooks/useIsExactMatch';

import { GrondslagTabs } from './-components/GrondslagTabs';
import { SidebarLayout } from './-components/SidebarLayout';

const searchSchema = z.object({
  p: z.number().optional().default(1),
});

type SearchParams = z.infer<typeof searchSchema>;

/**
 * Query options
 */
const sectiesQueryOptions = (search: SearchParams, mapId: string) => {
  return useStdMap.getOptions({
    page: search.p,
    size: 10,
    sort: [{ field: 'Naam', direction: 'asc' }],
    filter: [
      {
        field: 'Map',
        op: 'eq',
        value: mapId,
      },
    ],
  });
};

/**
 * Route
 */
export const Route = createFileRoute(
  '/beheer/standaardteksten/$grondslagId/$mapId'
)({
  validateSearch: searchSchema.parse,
  loaderDeps: ({ search }) => ({ search }),
  beforeLoad: ({ context, params }) => ({
    getTitle: () => {
      const map = context.queryClient.getQueryData<STDMapSchema>(
        useStdMapId.getKey(params.mapId)
      );

      return map?.Naam;
    },
  }),
  loader: async ({ context, params, deps: { search } }) => {
    const [map] = await Promise.all([
      context.queryClient.ensureQueryData(useStdMapId.getOptions(params.mapId)),
      context.queryClient.ensureQueryData(
        sectiesQueryOptions(search, params.mapId)
      ),
    ]);

    if (!map) throw notFound();
  },
  component: SectiesComponent,
});

/**
 * Route component
 */
function SectiesComponent() {
  const search = Route.useSearch();
  const { mapId, grondslagId } = Route.useParams();

  const secties = useQuery(sectiesQueryOptions(search, mapId));
  const map = useStdMapId(mapId);

  const patchMapId = usePatchStdMapId();

  /**
   * NOTE: Temporary solution for breadcrumbs
   *
   * When the route is not exactly this route, render the outlet
   */
  const isExactMatch = useIsExactMatch(Route.fullPath);

  if (!isExactMatch) {
    return <Outlet />;
  }

  if (!secties.data || !map.data) return null;

  return (
    <SidebarLayout testId={Route.fullPath}>
      <GrondslagTabs>
        <Explorer
          backButton={
            <TextButton
              className="mb-0.5 flex items-center"
              variant="gray"
              size="small"
              asChild
            >
              <Link
                from={Route.fullPath}
                to="/beheer/standaardteksten/$grondslagId"
                search={{ p: 1 }}
              >
                <RegularChevronRight
                  size={14}
                  className="mr-1 inline-block rotate-180"
                />
                <span>Terug</span>
              </Link>
            </TextButton>
          }
          title={
            <>
              {map.data.Naam}
              {map.data.Status === 'Archief' ? (
                <span className="font-normal">
                  {` `}(onzichtbaar voor gebruikers)
                </span>
              ) : (
                ''
              )}
            </>
          }
          button={
            <Button
              variant="secondary"
              onClick={() => {
                patchMapId.mutate({
                  id: mapId,
                  data: {
                    Status:
                      map.data.Status === 'Archief' ? 'Actief' : 'Archief',
                  },
                });
              }}
            >
              {map.data.Status === 'Archief'
                ? 'Maak map zichtbaar'
                : 'Maak map onzichtbaar'}
            </Button>
          }
        >
          {!secties.data.objects?.length ? (
            <Explorer.Placeholder>
              Deze map heeft geen secties
            </Explorer.Placeholder>
          ) : (
            secties.data.objects.map((sectie) => (
              <Fragment key={`sectie-${sectie.ID}`}>
                <Explorer.ItemHeading
                  infoCardProps={
                    sectie.Toelichting
                      ? {
                          description: sectie.Toelichting,
                          testId: `${sectie.Naam}-toelichting`,
                        }
                      : undefined
                  }
                  inactive={sectie.Status === 'Archief'}
                  title={sectie.Naam}
                  dropdownAriaLabel={sectie.Naam}
                  dropdownMenuContent={
                    <>
                      <ModalTrigger
                        modalComponent={StandaardtekstSectieEdit}
                        onOpen={() => ({ sectie })}
                        asChild
                      >
                        <DropdownMenu.Button>Wijzigen</DropdownMenu.Button>
                      </ModalTrigger>

                      <DropdownMenu.Button
                        onClick={() => {
                          patchMapId.mutate({
                            id: sectie.ID!,
                            data: {
                              Status:
                                sectie.Status === 'Archief'
                                  ? 'Actief'
                                  : 'Archief',
                            },
                          });
                        }}
                      >
                        {sectie.Status === 'Archief'
                          ? 'Zichtbaar maken'
                          : 'Onzichtbaar maken'}
                      </DropdownMenu.Button>
                    </>
                  }
                />
                <Explorer.Button
                  asChild
                  icon="file"
                  inactive={sectie.Status === 'Archief'}
                >
                  <Link
                    to="/beheer/standaardteksten/$grondslagId/$mapId/$sectieId/voorschriften"
                    params={{ mapId, sectieId: sectie.ID!, grondslagId }}
                    search={{ p: 1 }}
                  >
                    Voorschriften
                    {sectie.Status === 'Archief'
                      ? ' (onzichtbaar voor gebruikers)'
                      : ''}
                  </Link>
                </Explorer.Button>

                <Explorer.Button
                  icon="file"
                  asChild
                  inactive={sectie.Status === 'Archief'}
                >
                  <Link
                    to="/beheer/standaardteksten/$grondslagId/$mapId/$sectieId/inhoudelijke-overwegingen"
                    params={{ mapId, sectieId: sectie.ID!, grondslagId }}
                    search={{ p: 1 }}
                  >
                    Inhoudelijke overwegingen
                    {sectie.Status === 'Archief'
                      ? ' (onzichtbaar voor gebruikers)'
                      : ''}
                  </Link>
                </Explorer.Button>
              </Fragment>
            ))
          )}
        </Explorer>

        <ModalTrigger
          modalComponent={StandaardtekstSectieAdd}
          onOpen={() => ({ mapId, grondslagId })}
          asChild
        >
          <Button className="mt-3">Nieuwe sectie</Button>
        </ModalTrigger>
      </GrondslagTabs>

      {(secties.data.num_pages ?? 0) > 1 ? (
        <div className="mt-5 flex justify-center">
          <PaginationLinks
            aria-label="Sectie pagina's"
            renderLink={({ children, page }) => (
              <Link
                to="/beheer/standaardteksten/$grondslagId/$mapId"
                search={{ p: page }}
                params={{ mapId, grondslagId }}
              >
                {children}
              </Link>
            )}
            currentPage={search.p}
            totalPages={secties.data.num_pages ?? 0}
          />
        </div>
      ) : null}
    </SidebarLayout>
  );
}
